import { useEffect, useRef, useState } from "react";
import { TrendingUp } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Rolling counter for "Total Value Found — $X.XX".
 * - Value is the running sum of `report.prices.recommended` across all scans in this session
 * - Persists to localStorage so it survives refreshes
 * - Uses requestAnimationFrame count-up animation
 */
const LS_KEY = "rsell_ai_value_found_v1";

export function readTotalValue() {
  try { return parseFloat(localStorage.getItem(LS_KEY) || "0") || 0; } catch { return 0; }
}
export function bumpTotalValue(delta) {
  const current = readTotalValue();
  const next = Math.max(0, current + (Number.isFinite(delta) ? delta : 0));
  try { localStorage.setItem(LS_KEY, String(next)); } catch {}
  window.dispatchEvent(new CustomEvent("rsell:value-updated", { detail: next }));
  return next;
}
export function resetTotalValue() {
  try { localStorage.setItem(LS_KEY, "0"); } catch {}
  window.dispatchEvent(new CustomEvent("rsell:value-updated", { detail: 0 }));
}

export default function ValueFoundTicker() {
  const [target, setTarget] = useState(readTotalValue());
  const [display, setDisplay] = useState(target);
  const rafRef = useRef(0);
  const startRef = useRef({ from: target, to: target, t0: 0 });

  useEffect(() => {
    const onUpdate = (e) => setTarget(e.detail ?? readTotalValue());
    window.addEventListener("rsell:value-updated", onUpdate);
    return () => window.removeEventListener("rsell:value-updated", onUpdate);
  }, []);

  useEffect(() => {
    const from = display;
    const to = target;
    if (Math.abs(to - from) < 0.01) { setDisplay(to); return; }
    startRef.current = { from, to, t0: performance.now() };
    const duration = 900;
    const step = (now) => {
      const { from, to, t0 } = startRef.current;
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setDisplay(from + (to - from) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (target <= 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1F5F9] text-[#0F172A] whitespace-nowrap"
      data-testid="value-found-ticker"
      title="Total item value rsell ai has helped you identify"
    >
      <TrendingUp size={12} className="text-[#10B981]" />
      <span className="text-[10px] uppercase tracking-widest font-semibold text-[#64748B]">Value found</span>
      <span className="font-mono-num text-xs font-bold">
        ${display.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </motion.div>
  );
}
